// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useState, useCallback } from 'react'
import { Plus, Trash2, TrendingUp } from 'lucide-react'
import { Card }   from '@/components/ui/Card'
import { Input }  from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Toggle } from '@/components/ui/Toggle'
import { InfoButton } from '@/components/ui/InfoButton'
import { useWizardStore } from '@/store'
import { useAuthStore }   from '@/store'
import { useAutosave }    from '@/hooks/useAutosave'
import {
  loadInvestments, saveInvestments, EMPTY_INVESTMENTS, EMPTY_INVESTMENT,
  type InvestmentsData, type InvestmentEntry,
  NL_BROKERS, INTL_BROKERS, INVESTMENT_TYPES, CRYPTO_PLATFORMS,
} from '@/services/wizard'
import type { SaveStatus } from '@/hooks/useAutosave'

const COUNTRIES = [{ value: 'NL', label: 'Nederland' }, { value: 'BE', label: 'België' }, { value: 'DE', label: 'Duitsland' }, { value: 'UK', label: 'Verenigd Koninkrijk' }, { value: 'US', label: 'Verenigde Staten' }, { value: 'INTL', label: 'Internationaal / Anders' }]

export function NewStep07Investments({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { setAnswer } = useWizardStore()
  const { userId }    = useAuthStore()
  const [data,  setData]  = useState<InvestmentsData>(EMPTY_INVESTMENTS)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!userId) return
    loadInvestments(userId).then(d => { if (d) setData(d); setReady(true) })
  }, [userId])

  const save = useCallback(async (d: InvestmentsData) => { if (userId) await saveInvestments(userId, d) }, [userId])
  const { status } = useAutosave({ data, onSave: save, enabled: ready })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function setHas(v: string) {
    const investments = v === 'yes' && data.investments.length === 0 ? [{ ...EMPTY_INVESTMENT }] : data.investments
    setData({ hasInvestments: v, investments: v === 'yes' ? investments : [] })
    setAnswer('hasInvestments', v === 'yes')
  }

  function upd(i: number, field: keyof InvestmentEntry, value: string | boolean) {
    setData(p => { const investments = [...p.investments]; investments[i] = { ...investments[i], [field]: value }; return { ...p, investments } })
  }

  function brokerOptions(country: string) {
    return (country === 'NL' ? NL_BROKERS : INTL_BROKERS).map(b => ({ value: b, label: b }))
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2">
        <h2 className="text-xl font-semibold text-ink mb-1">Aandelen &amp; beleggingen</h2>
        <p className="text-sm text-ink-3">Zodat uw nabestaanden weten welke beleggingen u had en waar die staan.</p>
        <InfoButton title="Aandelen en beleggingen"
          content={<>
            <p>Beleggingen kunnen aandelen, obligaties, ETF's of crypto zijn. Na overlijden moet het depot worden overgedragen aan erfgenamen.</p>
            <p><strong>Depotblokkade:</strong> na noteer de gegevens blokkeert de broker het depot tijdelijk. Erfgenamen moeten een verklaring van erfrecht overleggen.</p>
            <p><strong>Crypto:</strong> zonder de wallet-sleutel zijn crypto-assets onbereikbaar. Zorg dat uw naasten weten waar de sleutel is.</p>
          </>} />
      </div>

      <div className="bg-white rounded-2xl border border-sand-300 p-5">
        <p className="text-base font-medium text-ink mb-4">Heeft u aandelen, beleggingen of crypto?</p>
        <div className="flex flex-col gap-2">
          {[{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nee' }].map(opt => (
            <button key={opt.value} type="button" onClick={() => setHas(opt.value)}
              className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${data.hasInvestments === opt.value ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'}`}>
              <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${data.hasInvestments === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
              <span className="text-base font-medium text-ink-2">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {data.hasInvestments === 'yes' && (
        <div className="flex flex-col gap-3 animate-slide-down">
          {data.investments.map((inv, i) => (
            <Card key={i}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-sage-600" /><p className="text-sm font-semibold text-ink">Belegging {i + 1}</p></div>
                {data.investments.length > 1 && (
                  <button onClick={() => setData(p => ({ ...p, investments: p.investments.filter((_, j) => j !== i) }))}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Crypto toggle */}
              <div className="flex items-center justify-between mb-3 bg-sand rounded-xl px-3 py-2.5">
                <span className="text-sm font-medium text-ink-2">Dit is cryptocurrency</span>
                <Toggle label="" checked={inv.isCrypto} onChange={v => upd(i, 'isCrypto', v)} />
              </div>

              {inv.isCrypto ? (
                <div className="flex flex-col gap-3">
                  <Select label="Platform / Exchange" options={CRYPTO_PLATFORMS.map(b => ({ value: b, label: b }))} value={inv.cryptoPlatform} onChange={e => upd(i, 'cryptoPlatform', e.target.value)} placeholder="Kies platform..." />
                  {inv.cryptoPlatform === 'Anders' && <Input label="Platform naam" value={inv.cryptoPlatformOther} onChange={e => upd(i, 'cryptoPlatformOther', e.target.value)} placeholder="Naam platform" />}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Select label="Land" options={COUNTRIES} value={inv.country} onChange={e => upd(i, 'country', e.target.value)} />
                    <Select label="Broker / Bank" options={brokerOptions(inv.country)} value={inv.brokerName} onChange={e => upd(i, 'brokerName', e.target.value)} placeholder="Kies broker..." />
                  </div>
                  {inv.brokerName === 'Anders' && <Input label="Naam broker" value={inv.brokerNameOther} onChange={e => upd(i, 'brokerNameOther', e.target.value)} placeholder="Naam broker" />}
                  <Select label="Type belegging" options={INVESTMENT_TYPES.map(t => ({ value: t, label: t }))} value={inv.investmentType} onChange={e => upd(i, 'investmentType', e.target.value)} placeholder="Kies type..." />
                  <div className="flex items-start gap-2.5">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-ink-2 mb-1.5">Hefboomproducten (turbo's, opties)?</label>
                      <div className="flex gap-2">
                        {['yes', 'no'].map(v => (
                          <button key={v} type="button" onClick={() => upd(i, 'hasLeverage', v)}
                            className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-all ${inv.hasLeverage === v ? 'border-sage-600 bg-sage-50 text-sage-800' : 'border-sand-300 text-ink-3 hover:border-sage-300'}`}>
                            {v === 'yes' ? 'Ja' : 'Nee'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-3">
                <label className="block text-sm font-medium text-ink-2 mb-1.5">Opmerkingen</label>
                <textarea value={inv.notes} onChange={e => upd(i, 'notes', e.target.value)}
                  placeholder="Bijv: Inloggegevens staan bij notaris, wallet-sleutel in kluis..."
                  className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm text-ink resize-none focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent min-h-[70px]"
                  maxLength={300} />
              </div>
            </Card>
          ))}
          <Button variant="outline" onClick={() => setData(p => ({ ...p, investments: [...p.investments, { ...EMPTY_INVESTMENT }] }))} icon={<Plus className="h-4 w-4" />}>
            Nog een belegging toevoegen
          </Button>
        </div>
      )}
    </div>
  )
}
